/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../../assets/ts/_utils'
import {useThemeMode} from '../../../layout/theme-mode/ThemeModeProvider'
import {useIntl} from 'react-intl'
import {useAuth} from '../../../../../app/modules/auth'
import {getGuidanceComparisonChart} from '../../../../../app/modules/guidance/guidances-list/core/_requests'
import {
  ChartModel,
  initialChartModel,
} from '../../../../../_metronic/partials/widgets/charts/_models'
import {useYear} from '../../../../../_metronic/partials/layout/header-menus/YearsManager'
import {RoleModel} from '../../../../../app/modules/auth/core/_models'
import { useTerm } from '../../../layout/header-menus/TermsManager'

type Props = {
  className: string
  unitId?: number
  customerId?: number
}

const GuidanceComparisonChart: React.FC<Props> = ({
  className,
  unitId = undefined,
  customerId = undefined,
}) => {
  const [guidanceComparisonChart, setGuidanceComparisonChart] = useState<ChartModel | undefined>(
    initialChartModel
  )
  const [refreshOnce, setRefreshOnce] = useState<boolean>(true)
  const chartRef = useRef<HTMLDivElement | null>(null)
  const {mode} = useThemeMode()
  const intl = useIntl()
  const {currentUser, auth} = useAuth()
  const currentYearId = useYear()
  const currentTermId = useTerm()
  useEffect(() => {
    let query = ''
    if (auth?.currentRole === RoleModel.Admin) query = '&academicYearId=' + currentYearId
    else if (auth?.currentRole === RoleModel.InstitutionManager)
      query = '&institutionId=' + currentUser?.institutionId + '&academicYearId=' + currentYearId
    else if (auth?.currentRole === RoleModel.Customer )
          query = '&customerId=' + currentUser?.customerId + '&academicYearId=' + currentYearId + '&termId=' + currentTermId;
        else if (auth?.currentRole === RoleModel.Person)
          query = '&personId=' + currentUser?.personId + '&academicYearId=' + currentYearId + '&termId=' + currentTermId;
    else
      query =
        '&institutionId=' +
        currentUser?.institutionId +
        '&subinstitutionId=' +
        currentUser?.subinstitutionId +
        '&academicYearId=' +
        currentYearId +
        '&currentTermId=' +
        currentTermId +
        '&employeeId=' +
        currentUser!.employeeId +
         (unitId ? "&unitId=" + unitId : '') +
          (customerId ? "&customerId=" + customerId : '');
    getGuidanceComparisonChart(query).then((value) => {
      setGuidanceComparisonChart(value)
      setTimeout(function () {
        setRefreshOnce(false)
      }, 500)
    })
  }, [className, unitId, customerId])

  const refreshMode = () => {
    if (!chartRef.current) {
      return
    }
    const height = parseInt(getCSS(chartRef.current, 'height'))
    const chart = new ApexCharts(chartRef.current, getChartOptions(height, guidanceComparisonChart))

    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshMode()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, mode, guidanceComparisonChart, refreshOnce])

  function getChartOptions(
    height: number,
    guidanceComparisonChart: ChartModel | undefined
  ): ApexOptions {
    const labelColor = getCSSVariableValue('--kt-gray-500')
    const borderColor = getCSSVariableValue('--kt-gray-200')
    const baseColor = '#1b77a1'
    const lightColor = getCSSVariableValue('--kt-info-light')
    const secondaryColor = '#1b77a1'

    return {
      series: [
        {
          name: intl.formatMessage({id: '6lo'}),
          data: guidanceComparisonChart?.series1 as Array<number>,
        },
        {
          name: intl.formatMessage({id: '6ln'}),
          data: guidanceComparisonChart?.series2 as Array<number>,
        },
      ],
      chart: {
        type: 'line', // Grafik türü çizgi grafiği olarak ayarlandı
        height: 350,
        stacked: true,
        toolbar: {
          show: true,
          tools: {
            download: true, // download icon aktif
            selection: false, // seçim aracı devre dışı
            zoom: false, // zoom aracı devre dışı
            zoomin: false, // zoom in devre dışı
            zoomout: false, // zoom out devre dışı
            pan: false, // pan devre dışı
            reset: false, // reset devre dışı
            customIcons: [], // ek özel ikonlar yok
          },
        },
      },
      stroke: {
        curve: 'smooth', // smooth (düzgün) eğri çizgileri
        width: 3, // Çizgi kalınlığı
      },
      fill: {
        type: 'solid',
        opacity: 2,
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100],
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val}`,
      },
      colors: ['#ffc702', '#723aea'],
      legend: {
        position: 'top',
        horizontalAlign: 'left',
      },
      xaxis: {
        categories: guidanceComparisonChart?.categories as Array<string>,
        type: 'datetime',
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          style: {
            colors: labelColor,
            fontSize: '12px',
          },
        },
        crosshairs: {
          position: 'front',
          stroke: {
            color: baseColor,
            width: 1,
            dashArray: 3,
          },
        },
        tooltip: {
          enabled: true,
          formatter: undefined,
          offsetY: 0,
          style: {
            fontSize: '12px',
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: labelColor,
            fontSize: '12px',
          },
        },
      },
      tooltip: {
        x: {
          format: 'dd/MM/',
        },
      },
      grid: {
        borderColor: borderColor,
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      markers: {
        strokeColors: baseColor,
        strokeWidth: 3,
      },
    }
  }

  return (
    <div className={`card ${className}`}>
      {/* begin::Header */}
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{intl.formatMessage({id: '6lp'})}</span>

          <span className='text-muted fw-semibold fs-7'>{intl.formatMessage({id: '6lr'})}</span>
        </h3>
      </div>
      {/* end::Header */}

      {/* begin::Body */}
      <div className='card-body'>
        {/* begin::Chart */}
        <div ref={chartRef} id='kt_charts_widget_3_chart' style={{height: '350px'}}></div>
        {/* end::Chart */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export {GuidanceComparisonChart}
